import { useQuery } from "@tanstack/react-query";
import { motion, type Variants } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Baby,
  BookMarked,
  Brain,
  BrainCircuit,
  Bug,
  CircleDot,
  Droplet,
  Droplets,
  FlaskConical,
  HeartPulse,
  Loader2,
  Scissors,
  Siren,
  Stethoscope,
  Target,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  type CaseRecipeSummary,
  casesApi,
} from "@/features/cases/cases.api";
import { difficultyToSeverity, specialtyIcon } from "@/features/cases/severity";
import { ApiError } from "@/lib/api";

const ICONS: Record<string, LucideIcon> = {
  Stethoscope,
  HeartPulse,
  Siren,
  Baby,
  Scissors,
  Brain,
  FlaskConical,
  Droplets,
  CircleDot,
  Bug,
  Target,
  BrainCircuit,
  Users,
  User,
  Droplet,
};

const stagger: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.08 },
  },
};

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

export function CasesListPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["cases"],
    queryFn: casesApi.list,
  });

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={stagger}
      className="space-y-8"
    >
      <motion.header variants={fadeUp} className="flex items-end justify-between">
        <div>
          <div className="font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
            SimuMed · Biblioteca de casos
          </div>
          <h1 className="mt-1 font-[var(--font-display)] text-4xl font-extrabold text-[var(--color-text-primary)]">
            Selecciona un caso clínico
          </h1>
          <p className="mt-2 max-w-2xl text-[var(--color-text-secondary)]">
            Cada caso se genera desde una receta estructurada. El paciente te responde turn-by-turn
            con el registro lingüístico correcto, el examen físico se resuelve por comando, y al
            cerrar comparas tu razonamiento con la traza del reasoner.
          </p>
        </div>
        <Badge variant="outline" className="hidden sm:inline-flex">
          Fase 1 · MVP
        </Badge>
      </motion.header>

      {isLoading && (
        <motion.div variants={fadeUp} className="flex items-center gap-2 text-[var(--color-text-muted)]">
          <Loader2 className="animate-spin" size={16} /> Cargando casos...
        </motion.div>
      )}

      {error && (
        <motion.div
          variants={fadeUp}
          className="rounded-xl border border-[var(--color-critical)]/30 bg-[var(--color-critical)]/10 px-4 py-3 text-[var(--color-critical)]"
        >
          Error: {error instanceof ApiError ? error.message : String(error)}
        </motion.div>
      )}

      {data && (
        <motion.div
          variants={stagger}
          className="grid grid-cols-1 gap-4 lg:grid-cols-2"
        >
          {data.map((c) => (
            <CaseCard key={c.id} recipe={c} />
          ))}
        </motion.div>
      )}
    </motion.div>
  );
}

function CaseCard({ recipe }: { recipe: CaseRecipeSummary }) {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const severity = difficultyToSeverity(recipe.difficulty);
  const IconCmp = ICONS[specialtyIcon(recipe.specialty)] ?? Stethoscope;

  async function onStart() {
    setStarting(true);
    try {
      const resp = await casesApi.start(recipe.id);
      navigate(`/app/cases/${resp.encounter.id}`);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo iniciar el caso",
      );
      setStarting(false);
    }
  }

  return (
    <motion.article
      variants={fadeUp}
      whileHover={{ y: -4 }}
      className="group relative overflow-hidden rounded-[var(--radius-2xl)] border border-[var(--color-border)] bg-[var(--color-surface)]/80 p-6 backdrop-blur-sm shadow-[var(--shadow-sm)] transition-all hover:border-[var(--color-blue)]/30 hover:shadow-[var(--shadow-md)]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full opacity-[0.08] blur-3xl transition-opacity group-hover:opacity-[0.18]"
        style={{
          background:
            severity.level === "critical"
              ? "var(--color-critical)"
              : severity.level === "urgent"
                ? "var(--color-urgent)"
                : "var(--color-stable)",
        }}
      />
      <div className="relative flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--color-blue)]/12 text-[var(--color-blue-soft)]">
          <IconCmp size={24} strokeWidth={1.7} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Badge variant={severity.variant} size="sm">
              {severity.label}
            </Badge>
            <span className="font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
              {recipe.specialty.replace(/_/g, " ")} · dif {recipe.difficulty}/5
            </span>
          </div>
          <h3 className="mt-2 font-[var(--font-display)] text-xl font-bold text-[var(--color-text-primary)]">
            {recipe.title}
          </h3>
          <p className="mt-1 text-[13px] text-[var(--color-text-secondary)]">
            {recipe.patient_summary}
          </p>
        </div>
      </div>

      <ul className="relative mt-5 space-y-1.5">
        {recipe.teaching_objectives.slice(0, 3).map((obj) => (
          <li
            key={obj}
            className="flex items-start gap-2 text-[12.5px] text-[var(--color-text-secondary)]"
          >
            <BookMarked
              size={12}
              className="mt-1 shrink-0 text-[var(--color-gold)]"
            />
            <span>{obj}</span>
          </li>
        ))}
      </ul>

      <div className="relative mt-6 flex items-center justify-between gap-3">
        <span className="font-[var(--font-mono)] text-[10px] text-[var(--color-text-dim)]">
          v{recipe.version}
        </span>
        <Button
          variant="primary"
          onClick={onStart}
          disabled={starting}
          className="shadow-[var(--shadow-glow-blue)]"
        >
          {starting ? (
            <>
              <Loader2 className="animate-spin" size={14} /> Generando caso...
            </>
          ) : (
            "Iniciar caso"
          )}
        </Button>
      </div>
    </motion.article>
  );
}
